import React, { useContext } from 'react';
import { ConfigurationContext, PublicConfigurationContext } from '../Context';
import { AidKitLogo, MiniAidKitLogo } from '../Util';

export type PortalHeaderProps = {
  /** Rendered in the left grid column (e.g. language dropdown or sidebar trigger). */
  leftSlot?: React.ReactNode;
  /** Rendered in the right grid column (e.g. avatar menu, log in link, cancel button). */
  rightSlot?: React.ReactNode;
};

/**
 * Shared header for applicant-portal pages. Renders the program logo centered between
 * configurable left and right slots so all portal pages stay visually aligned.
 */
export function PortalHeader({ leftSlot, rightSlot }: PortalHeaderProps) {
  const configuration = useContext(ConfigurationContext);
  const publicConfig = useContext(PublicConfigurationContext);

  const applicantFacingLogo = publicConfig.interface?.applicantFacingLogo?.url || configuration.applicant_facing_logo;
  const applicantFacingLogoWidth = publicConfig.interface?.applicantFacingLogo?.width || configuration.applicant_facing_logo_width || '150';
  const programName = publicConfig.name || configuration.program_name || 'AidKit Program';

  const logo = applicantFacingLogo
    ? <img className="w-auto max-w-[150px] max-h-[125px]" alt={programName} src={applicantFacingLogo} width={applicantFacingLogoWidth} />
    : (
      <>
        <div className="hidden sm:block"><AidKitLogo width={100} height={40} /></div>
        <div className="block sm:hidden"><MiniAidKitLogo width={40} height={40} /></div>
      </>
    );

  return (
    <header className="bg-white sticky top-0 z-10">
      <div className="w-full grid grid-cols-3 items-center max-w-7xl mx-auto py-2 px-2 sm:px-6 lg:px-8 border-t-0 border-l-0 border-r-0 border-b border-solid border-gray-200">
        <div className="justify-self-start">{leftSlot}</div>
        <div className="justify-self-center">{logo}</div>
        <div className="justify-self-end pr-2 sm:pr-4">{rightSlot}</div>
      </div>
    </header>
  );
}
